import type { ReactNode } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import GridContainer from "@/features/stage/components/add-edit-stage/confComponents/GridContainer";

export type SortableFieldGridItem = {
  /** Stable key — what the tenant-wide order stores (see useLeadViewFieldOrder). */
  key: string;
  node: ReactNode;
  /** Extra classes on the cell (e.g. `col-span-2`). */
  className?: string;
};

/**
 * The lead drawer's edit-mode field grid, drag-sortable for Super Admins.
 *
 * `items` arrive already in display order (the caller sorted them with
 * `useLeadViewFieldOrder.sortByOrder`); a drop hands the caller the full key
 * list in its new order, which it persists tenant-wide. Everyone else gets
 * the same grid with no grips — the order is an org layout, not a personal
 * one. Each cell carries its own grip (top-right, over the label row) so the
 * inputs themselves stay fully clickable; dragging the input would steal
 * text selection and the Select triggers.
 */
export default function SortableFieldGrid({
  items,
  columns = 2,
  canReorder,
  onReorder,
}: {
  items: SortableFieldGridItem[];
  columns?: number;
  canReorder: boolean;
  onReorder: (nextKeys: string[]) => void;
}) {
  const sensors = useSensors(
    // A small distance threshold keeps a plain click on the grip from
    // starting a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const keys = items.map((it) => it.key);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = keys.indexOf(String(active.id));
    const to = keys.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(keys, from, to));
  };

  const grid = (
    <GridContainer columns={columns}>
      {items.map((it) => (
        <SortableCell
          key={it.key}
          id={it.key}
          canReorder={canReorder}
          className={it.className}
        >
          {it.node}
        </SortableCell>
      ))}
    </GridContainer>
  );

  // Readers get the plain grid: no sensors, no context, nothing to announce.
  if (!canReorder) return grid;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={keys} strategy={rectSortingStrategy}>
        {grid}
      </SortableContext>
    </DndContext>
  );
}

function SortableCell({
  id,
  canReorder,
  className,
  children,
}: {
  id: string;
  canReorder: boolean;
  className?: string;
  children: ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !canReorder });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group/field ${isDragging ? "z-20" : ""} ${className ?? ""}`}
    >
      {children}
      {canReorder && (
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          aria-label="Drag to change this field's position"
          title="Drag to change this field's position — saved for your whole workspace"
          className="absolute -top-0.5 right-0 p-0.5 rounded-md text-[#b4bab7] opacity-60 group-hover/field:opacity-100 focus-visible:opacity-100 hover:text-[#1e302a] hover:bg-[#f0f2f1] cursor-grab active:cursor-grabbing touch-none transition-opacity"
        >
          <GripVertical className="size-3.5" />
        </button>
      )}
    </div>
  );
}
